import { assertNever } from '@ofmos/shared/exhaustive';
import { useState } from 'react';
import type { FieldMetadata, FieldProvenance, FieldValue } from '../../data/client';
import { useUpdateRecord } from '../../data/hooks';
import { useToast } from '../../ui';

/**
 * Saving one field, and telling the truth about what happened to it.
 *
 * A write has three outcomes (see `WriteResult`) and only ONE of them means the
 * value on screen is the value in the database. `pending_approval` (INV-10) and
 * `write_blocked` (INV-5) therefore leave a marker ON the field: a toast is gone in
 * five seconds, and the whole reason those two outcomes exist is that somebody has
 * to notice them.
 */
export type FieldWriteMarker =
  | { readonly kind: 'awaiting_approval'; readonly attempted: FieldValue }
  | {
      readonly kind: 'refused';
      readonly attempted: FieldValue;
      readonly heldBy?: FieldProvenance | undefined;
    };

/**
 * WHICH OF THE FOUR ENDINGS THE WRITE TOOK, for the one caller that has to say something
 * different about each (`phase-b-coach.md` D4).
 *
 * The three field surfaces before the checklist all treated a write the same way: the row
 * closes, the marker says what happened, the toast is this hook's. Marking a checkpoint done
 * is the first write that has a SECOND consequence - it completes the playbook's task and
 * reports both in one sentence - and that consequence must not fire on a write the server
 * refused. So the promise resolves with the ending instead of with nothing; callers that do
 * not care ignore it exactly as they did.
 */
export type FieldWriteOutcome = 'applied' | 'pending_approval' | 'write_blocked' | 'failed';

export interface RecordFieldSave {
  /** Resolves when the write has SETTLED, with the ending it took. */
  readonly save: (field: FieldMetadata, next: FieldValue) => Promise<FieldWriteOutcome>;
  /** Keyed by field UUID. Cleared for a field once a write to it applies. */
  readonly markers: Readonly<Record<string, FieldWriteMarker>>;
  readonly savingFieldId: string | null;
}

function holderName(provenance: FieldProvenance | undefined): string {
  if (provenance === undefined) {
    return 'A person';
  }
  return provenance.displayName ?? provenance.userId;
}

export function useRecordFieldSave(objectId: string, recordId: string): RecordFieldSave {
  const toast = useToast();
  const updateRecord = useUpdateRecord(objectId);
  const [markers, setMarkers] = useState<Readonly<Record<string, FieldWriteMarker>>>({});
  const [savingFieldId, setSavingFieldId] = useState<string | null>(null);

  const setMarker = (fieldId: string, marker: FieldWriteMarker) => {
    setMarkers((current) => ({ ...current, [fieldId]: marker }));
  };

  const clearMarker = (fieldId: string) => {
    setMarkers((current) =>
      current[fieldId] === undefined
        ? current
        : Object.fromEntries(Object.entries(current).filter(([id]) => id !== fieldId)),
    );
  };

  /**
   * IT RESOLVES RATHER THAN RETURNING, and one caller needs that: a FILES field cannot show
   * an attachment's preview until the write that LINKS the file has come back (wave 1c,
   * finding 3). `onSettled` and not `onSuccess`, because a refused or failed write is also
   * an answer - the field has to stop waiting either way.
   */
  const save = (field: FieldMetadata, next: FieldValue): Promise<FieldWriteOutcome> =>
    new Promise((settled) => {
      setSavingFieldId(field.id);
      /**
       * The ending, recorded as the callbacks run and read by `onSettled`, which is the only
       * one of the three that fires on every path. `failed` is the default rather than a
       * fifth callback: a mutation that neither succeeded nor errored cannot reach here.
       */
      let outcome: FieldWriteOutcome = 'failed';
      updateRecord.mutate(
        { recordId, values: { [field.id]: next } },
        {
          onSuccess: (result) => {
            outcome = result.status;
            switch (result.status) {
              case 'applied':
                // Quietly: the value standing in the row IS the confirmation. A
                // toast per keystroke-sized edit trains people to ignore toasts.
                clearMarker(field.id);
                return;
              case 'pending_approval':
                setMarker(field.id, { kind: 'awaiting_approval', attempted: next });
                toast({
                  title: 'Queued for approval',
                  description: `${field.label} keeps its saved value until someone approves the change.`,
                });
                return;
              case 'write_blocked': {
                const blocked = result.blocked.find((entry) => entry.fieldId === field.id);
                setMarker(field.id, {
                  kind: 'refused',
                  attempted: next,
                  ...(blocked !== undefined ? { heldBy: blocked.heldBy } : {}),
                });
                toast({
                  title: `${field.label} was not saved`,
                  description: `${holderName(blocked?.heldBy)} corrected this field, so automated writers cannot overwrite it.`,
                  tone: 'danger',
                });
                return;
              }
              default:
                return assertNever(result, 'useRecordFieldSave.save');
            }
          },
          onError: (error) => {
            toast({
              title: `Could not save ${field.label}`,
              description: error.message,
              tone: 'danger',
            });
          },
          onSettled: () => {
            setSavingFieldId(null);
            settled(outcome);
          },
        },
      );
    });

  return { save, markers, savingFieldId };
}
